const catalogueTagName = 'jesoli-featured-catalogue';
const catalogueSelector = `${catalogueTagName}[data-section-id]`;
const mobileMediaQuery = window.matchMedia('(max-width: 749px)');
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const coarsePointerQuery = window.matchMedia('(pointer: coarse)');
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class JesoliFeaturedCatalogue extends HTMLElement {
  #abortController;
  #carousels = [];
  #initialized = false;
  #resizeObserver;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#carousels = [...this.querySelectorAll('[data-catalogue-collection]')]
      .map((collection) => this.#createCarouselState(collection))
      .filter(Boolean);

    for (const state of this.#carousels) this.#bindCarousel(state, signal);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const state = this.#carousels.find((item) => item.track === entry.target);
          if (state) this.#scheduleLayout(state);
        }
      });
      for (const state of this.#carousels) this.#resizeObserver.observe(state.track);
    } else {
      window.addEventListener('resize', this.#handleWindowResize, { signal });
    }

    mobileMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#enhanceProductCardTitles();
    this.#ensureUniqueIds();
    this.#activateCategoryFromUrl();
    window.requestAnimationFrame(() => this.#refreshAll());
  }

  destroy() {
    if (!this.#initialized) return;

    this.#abortController?.abort();
    mobileMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#resizeObserver?.disconnect();

    for (const state of this.#carousels) {
      if (state.layoutFrame !== undefined) window.cancelAnimationFrame(state.layoutFrame);
      if (state.scrollFrame !== undefined) window.cancelAnimationFrame(state.scrollFrame);
      window.clearTimeout(state.clickResetTimer);
    }

    this.#abortController = undefined;
    this.#resizeObserver = undefined;
    this.#carousels = [];
    this.#initialized = false;
  }

  selectBlock(blockId) {
    const collection = this.querySelector(`[data-block-id="${CSS.escape(String(blockId || ''))}"]`);
    if (!(collection instanceof HTMLElement)) return false;

    this.#focusCollection(collection, { focus: false, scroll: true });
    return true;
  }

  selectCategory(key, { focus = false, scroll = false } = {}) {
    const collection = this.#findCollection(key);
    if (!collection) return false;

    this.#focusCollection(collection, { focus, scroll });
    return true;
  }

  hasCategory(key) {
    return Boolean(this.#findCollection(key));
  }

  #createCarouselState(collection) {
    if (!(collection instanceof HTMLElement)) return null;

    const track = collection.querySelector('[data-carousel-track]');
    if (!(track instanceof HTMLElement)) return null;

    const desktopCards = Number.parseInt(collection.dataset.carouselDesktopCards || '4', 10);
    const previewRatio = Number.parseFloat(collection.dataset.carouselPreviewRatio || '0');

    return {
      collection,
      track,
      previous: collection.querySelector('[data-carousel-previous]'),
      next: collection.querySelector('[data-carousel-next]'),
      progress: collection.querySelector('[data-carousel-progress]'),
      progressTrack: collection.querySelector('[data-carousel-progress-track]'),
      progressThumb: collection.querySelector('[data-carousel-progress-thumb]'),
      desktopCards: Number.isFinite(desktopCards) ? desktopCards : 4,
      previewRatio: Number.isFinite(previewRatio) ? previewRatio : 0,
      layoutFrame: undefined,
      scrollFrame: undefined,
      clickResetTimer: undefined,
      pointerId: undefined,
      startX: 0,
      startScrollLeft: 0,
      dragging: false,
      suppressClick: false,
    };
  }

  #bindCarousel(state, signal) {
    state.previous?.addEventListener('click', () => this.#scrollCarousel(state, -1), { signal });
    state.next?.addEventListener('click', () => this.#scrollCarousel(state, 1), { signal });
    state.track.addEventListener('scroll', () => this.#scheduleControls(state), { passive: true, signal });
    state.track.addEventListener('keydown', (event) => this.#handleTrackKeydown(event, state), { signal });
    state.track.addEventListener('pointerdown', (event) => this.#handlePointerDown(event, state), { signal });
    window.addEventListener('pointermove', (event) => this.#handlePointerMove(event, state), { signal });
    window.addEventListener('pointerup', (event) => this.#finishPointerDrag(event, state), { signal });
    window.addEventListener('pointercancel', (event) => this.#finishPointerDrag(event, state), { signal });
    state.track.addEventListener('click', (event) => this.#suppressDraggedClick(event, state), {
      capture: true,
      signal,
    });
    state.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  #findCollection(key) {
    const normalizedKey = this.#normalizeCategoryKey(key === 'all' ? 'shop-all' : key);
    if (!normalizedKey) return undefined;

    return this.#carousels.find((state) => {
      const candidates = [
        state.collection.dataset.catalogueKey,
        state.collection.dataset.catalogueAlias,
        state.collection.dataset.catalogueTitle,
      ];
      return candidates.some((candidate) => this.#normalizeCategoryKey(candidate) === normalizedKey);
    })?.collection;
  }

  #normalizeCategoryKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #focusCollection(collection, { focus, scroll }) {
    if (scroll) {
      const behavior = reducedMotionQuery.matches ? 'auto' : 'smooth';
      collection.scrollIntoView({ behavior, block: 'start' });
    }

    if (focus) {
      window.requestAnimationFrame(() => collection.focus({ preventScroll: true }));
    }
  }

  #handleTrackKeydown(event, state) {
    if (event.target !== state.track) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.#scrollCarousel(state, event.key === 'ArrowLeft' ? -1 : 1);
  }

  #handlePointerDown(event, state) {
    if (!finePointerQuery.matches || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, input, select, textarea')) return;

    state.pointerId = event.pointerId;
    state.startX = event.clientX;
    state.startScrollLeft = state.track.scrollLeft;
    state.dragging = false;
    state.suppressClick = false;
  }

  #handlePointerMove(event, state) {
    if (state.pointerId !== event.pointerId) return;

    const distance = event.clientX - state.startX;
    if (!state.dragging && Math.abs(distance) < 7) return;

    if (!state.dragging) state.track.setPointerCapture?.(event.pointerId);
    state.dragging = true;
    state.track.classList.add('is-dragging');
    state.track.scrollLeft = state.startScrollLeft - distance;
    event.preventDefault();
  }

  #finishPointerDrag(event, state) {
    if (state.pointerId !== event.pointerId) return;

    if (state.track.hasPointerCapture?.(event.pointerId)) {
      state.track.releasePointerCapture(event.pointerId);
    }
    state.pointerId = undefined;
    state.track.classList.remove('is-dragging');

    if (!state.dragging) return;

    state.dragging = false;
    state.suppressClick = true;
    this.#snapToNearestCard(state);
    window.clearTimeout(state.clickResetTimer);
    state.clickResetTimer = window.setTimeout(() => {
      state.suppressClick = false;
      state.clickResetTimer = undefined;
    }, 180);
  }

  #suppressDraggedClick(event, state) {
    if (!state.suppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    state.suppressClick = false;
  }

  #snapToNearestCard(state) {
    const items = [...state.track.querySelectorAll('[data-catalogue-product]')];
    const firstItem = items[0];
    if (!(firstItem instanceof HTMLElement)) return;

    const origin = firstItem.offsetLeft;
    const current = state.track.scrollLeft;
    let closest = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      const target = item.offsetLeft - origin;
      const distance = Math.abs(target - current);
      if (distance < closestDistance) {
        closest = target;
        closestDistance = distance;
      }
    }

    state.track.scrollTo({
      left: closest,
      behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  #scrollCarousel(state, direction) {
    const items = [...state.track.querySelectorAll('[data-catalogue-product]')];
    const firstItem = items[0];
    const secondItem = items[1];
    if (!(firstItem instanceof HTMLElement)) return;

    const computedGap = Number.parseFloat(window.getComputedStyle(state.track).columnGap || '0');
    const step =
      secondItem instanceof HTMLElement ? secondItem.offsetLeft - firstItem.offsetLeft : firstItem.offsetWidth + computedGap;
    const cardsPerClick = state.track.clientWidth >= 900 ? 2 : 1;

    state.track.scrollBy({
      left: direction * step * cardsPerClick,
      behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  #scheduleLayout(state) {
    if (state.layoutFrame !== undefined) return;

    state.layoutFrame = window.requestAnimationFrame(() => {
      state.layoutFrame = undefined;
      this.#updateLayout(state);
    });
  }

  #scheduleControls(state) {
    if (state.scrollFrame !== undefined) return;

    state.scrollFrame = window.requestAnimationFrame(() => {
      state.scrollFrame = undefined;
      this.#updateControls(state);
    });
  }

  #updateLayout(state) {
    if (mobileMediaQuery.matches) {
      state.track.style.removeProperty('--jesoli-carousel-card-width');
      this.#updateControls(state);
      return;
    }

    const configuredCards = Math.max(1, state.desktopCards);
    const fullCards = state.track.clientWidth < 900 ? Math.min(configuredCards, 3) : configuredCards;
    const itemCount = state.track.querySelectorAll('[data-catalogue-product]').length;
    const previewRatio = itemCount > fullCards ? state.previewRatio : 0;
    const trackStyles = window.getComputedStyle(state.track);
    const gap = Number.parseFloat(trackStyles.columnGap || '0');
    const paddingLeft = Number.parseFloat(trackStyles.paddingLeft || '0');
    const paddingRight = Number.parseFloat(trackStyles.paddingRight || '0');
    const visibleGapCount = previewRatio > 0 ? fullCards : Math.max(0, fullCards - 1);
    const horizontalPadding = previewRatio > 0 ? paddingLeft : paddingLeft + paddingRight;
    const availableWidth = state.track.clientWidth - horizontalPadding - gap * visibleGapCount;
    const cardWidth = availableWidth / (fullCards + previewRatio);

    if (Number.isFinite(cardWidth) && cardWidth > 0) {
      state.track.style.setProperty('--jesoli-carousel-card-width', `${cardWidth}px`);
    }

    this.#updateControls(state);
  }

  #updateControls(state) {
    const maxScroll = Math.max(0, state.track.scrollWidth - state.track.clientWidth);
    const atStart = state.track.scrollLeft <= 2;
    const atEnd = state.track.scrollLeft >= maxScroll - 2;
    const hasOverflow = maxScroll > 2;
    const itemCount = state.track.querySelectorAll('[data-catalogue-product]').length;

    if (state.previous instanceof HTMLButtonElement) {
      state.previous.disabled = !hasOverflow || atStart;
      state.previous.setAttribute('aria-disabled', String(state.previous.disabled));
    }

    if (state.next instanceof HTMLButtonElement) {
      state.next.disabled = !hasOverflow || atEnd;
      state.next.setAttribute('aria-disabled', String(state.next.disabled));
    }

    this.#updateProgress(state, { hasOverflow, itemCount, maxScroll });
  }

  #updateProgress(state, { hasOverflow, itemCount, maxScroll }) {
    if (!(state.progress instanceof HTMLElement)) return;

    const shouldShow = hasOverflow && (mobileMediaQuery.matches || coarsePointerQuery.matches || itemCount >= 5);
    state.progress.hidden = !shouldShow;
    if (!shouldShow) return;
    if (!(state.progressTrack instanceof HTMLElement) || !(state.progressThumb instanceof HTMLElement)) return;

    const progressWidth = state.progressTrack.clientWidth;
    if (progressWidth <= 0 || state.track.scrollWidth <= 0) return;

    const visibleRatio = Math.min(1, state.track.clientWidth / state.track.scrollWidth);
    const thumbWidth = Math.max(18, progressWidth * visibleRatio);
    const scrollRatio = Math.min(1, Math.max(0, state.track.scrollLeft / maxScroll));
    const thumbOffset = Math.max(0, progressWidth - thumbWidth) * scrollRatio;

    state.progressThumb.style.setProperty('--jesoli-carousel-progress-width', `${thumbWidth}px`);
    state.progressThumb.style.setProperty('--jesoli-carousel-progress-offset', `${thumbOffset}px`);
  }

  #refreshAll() {
    for (const state of this.#carousels) this.#updateLayout(state);
  }

  #handleWindowResize = () => {
    this.#refreshAll();
  };

  #handleBreakpointChange = () => {
    this.#refreshAll();
  };

  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target !== this && !this.contains(target) && !target.contains(this)) return;

    this.selectBlock(event.detail?.blockId);
  };

  #enhanceProductCardTitles() {
    const cards = [...this.querySelectorAll('product-card')];
    const chooseOptionsLabel = this.dataset.chooseOptionsLabel || '';

    cards.forEach((card, cardIndex) => {
      const productId = card.dataset.productId;
      if (productId) card.id = `JesoliProductCard-${this.dataset.sectionId}-${productId}-${cardIndex}`;

      const fullTitle = card.querySelector('.product-card__link .visually-hidden')?.textContent?.trim();
      const titleLink = card.querySelector('a[ref="productTitleLink"]');
      if (fullTitle && titleLink instanceof HTMLAnchorElement) titleLink.title = fullTitle;

      const chooseButton = card.querySelector('.quick-add__button--choose');
      const chooseButtonLabel = chooseButton?.querySelector('.add-to-cart-text__content > span > span');
      if (chooseOptionsLabel && chooseButton instanceof HTMLButtonElement && chooseButtonLabel) {
        chooseButtonLabel.textContent = chooseOptionsLabel;
        if (fullTitle) chooseButton.setAttribute('aria-label', `${chooseOptionsLabel}: ${fullTitle}`);
      }
    });
  }

  #ensureUniqueIds() {
    const seenIds = new Set();
    let duplicateIndex = 0;

    for (const element of this.querySelectorAll('[id]')) {
      const originalId = element.id;
      if (!seenIds.has(originalId)) {
        seenIds.add(originalId);
        continue;
      }

      duplicateIndex += 1;
      const replacementId = `${originalId}-${this.dataset.sectionId}-${duplicateIndex}`;
      element.id = replacementId;
      seenIds.add(replacementId);

      const scope = element.closest('product-card') || this;
      const attributes = ['for', 'form', 'aria-controls', 'aria-describedby', 'aria-labelledby'];

      for (const candidate of scope.querySelectorAll('[for], [form], [aria-controls], [aria-describedby], [aria-labelledby], [href]')) {
        for (const attribute of attributes) {
          const value = candidate.getAttribute(attribute);
          if (!value) continue;

          const tokens = value.split(/\s+/).map((token) => (token === originalId ? replacementId : token));
          candidate.setAttribute(attribute, tokens.join(' '));
        }

        if (candidate.getAttribute('href') === `#${originalId}`) candidate.setAttribute('href', `#${replacementId}`);
      }
    }
  }

  #activateCategoryFromUrl() {
    const key = new URL(window.location.href).searchParams.get('catalogue');
    if (!key) return;

    window.requestAnimationFrame(() => {
      this.selectCategory(key, { focus: false, scroll: window.location.hash === '#shop' });
    });
  }
}

if (!customElements.get(catalogueTagName)) {
  customElements.define(catalogueTagName, JesoliFeaturedCatalogue);
}

const getCatalogueSections = (target) => {
  if (!(target instanceof Element)) return [];

  const sections = [...target.querySelectorAll(catalogueSelector)];
  if (target.matches(catalogueSelector)) sections.unshift(target);
  return sections;
};

document.addEventListener('shopify:section:load', (event) => {
  for (const section of getCatalogueSections(event.target)) section.initialize?.();
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const section of getCatalogueSections(event.target)) section.destroy?.();
});
